import { useState } from 'react';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Material } from '@models/material.model';
import { graphQlClient } from '@config/graphqlClient';
import MaterialGraphQL from '@graphql/material.queries';

interface CreateMaterialParams {
  name: string;
  supplier: string;
  time: string;
  amount: number;
  costPerUnit: number;
  pricePerUnit?: number | null;
  amountType: string;
  amountSystem: string;
  taskId: string;
  chargeToClient: boolean;
}

interface CreateMaterialResponse {
  createMaterial: Omit<Material, 'materialId'>;
}

interface UseCreateMaterialReturn {
  createMaterial: (params: CreateMaterialParams) => Promise<Material | null>;
  loading: boolean;
}

export const useCreateMaterial = (): UseCreateMaterialReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const createMaterial = async (
    params: CreateMaterialParams,
  ): Promise<Material | null> => {
    setLoading(true);

    try {
      const materialData = {
        name: params.name,
        supplier: params.supplier,
        time: params.time,
        amount: params.amount,
        costPerUnit: params.costPerUnit,
        pricePerUnit: params.pricePerUnit,
        amountType: params.amountType,
        amountSystem: params.amountSystem,
        taskId: params.taskId,
        chargeToClient: params.chargeToClient,
      };

      const data: CreateMaterialResponse = await graphQlClient.request(
        MaterialGraphQL.mutations.createMaterial,
        {
          material: materialData,
        },
      );

      if (data?.createMaterial) {
        dispatch(
          appendActionMessage({
            message: localeCommon['createdSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );

        // Generate a temporary ID for local state management
        const materialWithTempId: Material = {
          ...data.createMaterial,
          materialId: `temp-${Date.now()}`,
        };

        return materialWithTempId;
      }

      return null;
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message:
            error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { createMaterial, loading };
};
